/**
 * Notification Bell Component
 * Shows notification count and dropdown with recent notifications
 */

import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  UserPlus,
  MessageSquare,
  MessageCircle,
  CheckCircle,
  Coins,
  Settings,
  Info,
  AlertTriangle,
  XCircle,
  Check,
  Trash2,
  BellOff,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useNotificationStore } from "../stores/notificationStore";
import type { Notification, NotificationType } from "../types";
import { NOTIFICATION_COLORS } from "../types";

const NOTIFICATION_ICON_MAP: Record<NotificationType, React.ElementType> = {
  new_lead: UserPlus,
  session_started: MessageSquare,
  session_message: MessageCircle,
  session_ended: CheckCircle,
  credits_assigned: Coins,
  system: Settings,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Teraz";
  if (diffMins < 60) return `${diffMins} min temu`;
  if (diffHours < 24) return `${diffHours} godz. temu`;
  if (diffDays < 7) return `${diffDays} dni temu`;

  return date.toLocaleDateString("pl-PL", { day: "numeric", month: "short" });
}

function NotificationItem({
  notification,
  onClose,
}: {
  notification: Notification;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { markAsRead, removeNotification } = useNotificationStore();

  const Icon = NOTIFICATION_ICON_MAP[notification.type];
  const colorClasses = NOTIFICATION_COLORS[notification.type];

  const handleClick = () => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeNotification(notification.id);
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors",
        !notification.read && "bg-blue-50/50"
      )}
      onClick={handleClick}
    >
      <div className={cn("p-2 rounded-full shrink-0", colorClasses)}>
        <Icon className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "text-sm font-medium text-gray-900 truncate",
              !notification.read && "font-semibold"
            )}
          >
            {notification.title}
          </p>
          <button
            onClick={handleRemove}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
            aria-label="Usuń powiadomienie"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {formatTimeAgo(notification.timestamp)}
        </p>
      </div>

      {!notification.read && (
        <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-2" />
      )}
    </div>
  );
}

export function NotificationBell() {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    unreadCount,
    isDropdownOpen,
    setDropdownOpen,
    markAllAsRead,
    clearAll,
    getRecentNotifications,
  } = useNotificationStore();

  const recentNotifications = getRecentNotifications(10);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, setDropdownOpen]);

  // Close on Escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isDropdownOpen, setDropdownOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setDropdownOpen(!isDropdownOpen)}
        className={cn(
          "relative p-2 rounded-lg transition-colors",
          isDropdownOpen
            ? "bg-gray-100 text-gray-900"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        )}
        aria-label={`Powiadomienia${unreadCount > 0 ? ` (${unreadCount} nieprzeczytanych)` : ""}`}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5" />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Powiadomienia</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                <Check className="h-3 w-3" />
                Oznacz jako przeczytane
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <BellOff className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Brak powiadomień</p>
                <p className="text-xs text-gray-400 mt-1">
                  Tutaj pojawią się nowe powiadomienia
                </p>
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClose={() => setDropdownOpen(false)}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50">
              <span className="text-xs text-gray-500">
                {notifications.length} powiadomień
              </span>
              <button
                onClick={clearAll}
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
              >
                <Trash2 className="h-3 w-3" />
                Wyczyść wszystko
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
